import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { useToast } from "@/hooks/use-toast";
import { Loader2, Plus, Pencil, Link2, ExternalLink, ShieldCheck, ShieldAlert, Search, BookOpenCheck, AlertTriangle } from "lucide-react";

const PILLARS = [
  { n: 1, label: "Understanding Menopause" },
  { n: 2, label: "Symptoms" },
  { n: 3, label: "Treatments" },
  { n: 4, label: "Healthy Ageing" },
  { n: 5, label: "Lifestyle" },
  { n: 6, label: "Mental Health" },
  { n: 7, label: "Relationships" },
  { n: 8, label: "Diversity" },
  { n: 9, label: "Medicines" },
  { n: 10, label: "Practical Guides" },
];

const TYPES = ["symptom", "treatment", "medicine", "concept", "practical", "lifestyle", "condition"];
const RELATIONS = [
  "related_symptom", "related_treatment", "related_medicine", "related_concept",
  "related_article", "related_lesson", "related_question", "contraindicates", "alternative_to",
];

const CONTENT_GRADES = [
  { code: "guideline_backed", label: "🟢 Guideline-backed" },
  { code: "clinician_reviewed", label: "🟡 Clinician-reviewed" },
  { code: "editorial", label: "⚪ Editorial (pending clinical review)" },
];
const EVIDENCE_CLASSES = ["primary_source", "supporting", "background", "consumer_leaflet"];
const MAPPING_STATUSES = ["draft", "approved", "rejected"];

type Topic = {
  id: string;
  slug: string;
  title: string;
  pillar: number;
  topic_type: string;
  summary_plain: string;
  body_md: string | null;
  evidence_level: string | null;
  evidence_rationale: string | null;
  red_flags_md: string | null;
  doctor_questions_md: string | null;
  status: string;
  reviewed_at: string | null;
  next_review_at: string | null;
  plain_language_summary: string | null;
  author_name: string | null;
  clinical_reviewer_name: string | null;
  clinical_review_date: string | null;
  content_grade: string | null;
  originality_confirmed: boolean | null;
};

type Tier = { code: string; label: string; emoji: string };
type SourceDoc = { id: string; title: string; organisation: string; approved_for_rag: boolean | null };
type Mapping = {
  id: string;
  claim: string;
  her_season_wording: string | null;
  document_id: string | null;
  source_location: string | null;
  evidence_class: string | null;
  approval_status: string;
  reviewer_notes: string | null;
};
type SimilarityHit = {
  chunk_id: string;
  document_id: string;
  document_title: string;
  organisation: string;
  heading_path: string | null;
  content_excerpt: string;
  similarity: number;
};

const empty: Partial<Topic> = {
  slug: "", title: "", pillar: 1, topic_type: "concept",
  summary_plain: "", body_md: "", evidence_level: "moderate",
  evidence_rationale: "", red_flags_md: "", doctor_questions_md: "",
  status: "draft",
  plain_language_summary: "", author_name: "", clinical_reviewer_name: "",
  clinical_review_date: null, content_grade: "editorial", originality_confirmed: false,
};

export default function KnowledgeTopicsAdmin() {
  const { toast } = useToast();
  const [topics, setTopics] = useState<Topic[]>([]);
  const [tiers, setTiers] = useState<Tier[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<Partial<Topic> | null>(null);
  const [saving, setSaving] = useState(false);
  const [linksFor, setLinksFor] = useState<Topic | null>(null);
  const [links, setLinks] = useState<any[]>([]);
  const [newLink, setNewLink] = useState({ to: "", relation: "related_symptom" });
  const [filter, setFilter] = useState<{ pillar: string; status: string }>({ pillar: "all", status: "all" });
  const [sourceDocs, setSourceDocs] = useState<SourceDoc[]>([]);
  const [mappings, setMappings] = useState<Mapping[]>([]);
  const [newMapping, setNewMapping] = useState<Partial<Mapping>>({ claim: "", her_season_wording: "", document_id: "", source_location: "", evidence_class: "primary_source", approval_status: "draft" });
  const [scanning, setScanning] = useState(false);
  const [similarity, setSimilarity] = useState<SimilarityHit[] | null>(null);

  const load = async () => {
    setLoading(true);
    const [{ data: t }, { data: tr }, { data: sd }] = await Promise.all([
      supabase.from("knowledge_topics").select("*").order("pillar").order("title"),
      supabase.from("knowledge_evidence_tiers").select("*").order("sort_order"),
      supabase.from("medical_documents").select("id, title, organisation, approved_for_rag").order("title"),
    ]);
    setTopics((t ?? []) as Topic[]);
    setTiers((tr ?? []) as Tier[]);
    setSourceDocs((sd ?? []) as SourceDoc[]);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const loadMappings = async (topicId: string) => {
    const { data } = await supabase
      .from("knowledge_evidence_mappings")
      .select("*")
      .eq("topic_id", topicId)
      .order("created_at", { ascending: true });
    setMappings((data ?? []) as Mapping[]);
  };

  useEffect(() => {
    if (editing?.id) loadMappings(editing.id);
    else setMappings([]);
    setSimilarity(null);
  }, [editing?.id]);

  const addMapping = async () => {
    if (!editing?.id || !newMapping.claim) return;
    const { error } = await supabase.from("knowledge_evidence_mappings").insert({
      topic_id: editing.id,
      claim: newMapping.claim,
      her_season_wording: newMapping.her_season_wording || null,
      document_id: newMapping.document_id || null,
      source_location: newMapping.source_location || null,
      evidence_class: newMapping.evidence_class || null,
      approval_status: newMapping.approval_status || "draft",
    });
    if (error) { toast({ title: "Mapping failed", description: error.message, variant: "destructive" }); return; }
    setNewMapping({ claim: "", her_season_wording: "", document_id: "", source_location: "", evidence_class: "primary_source", approval_status: "draft" });
    loadMappings(editing.id);
  };

  const updateMappingStatus = async (id: string, status: string) => {
    const patch: any = { approval_status: status };
    if (status === "approved") patch.approved_at = new Date().toISOString();
    await supabase.from("knowledge_evidence_mappings").update(patch).eq("id", id);
    if (editing?.id) loadMappings(editing.id);
  };

  const removeMapping = async (id: string) => {
    await supabase.from("knowledge_evidence_mappings").delete().eq("id", id);
    if (editing?.id) loadMappings(editing.id);
  };

  const runSimilarityScan = async () => {
    if (!editing?.body_md || editing.body_md.length < 40) {
      toast({ title: "Add more body text first" });
      return;
    }
    setScanning(true);
    try {
      const { data, error } = await supabase.rpc("scan_topic_similarity", {
        p_body: editing.body_md,
        p_min_similarity: 0.35,
        p_limit: 15,
      });
      if (error) throw error;
      setSimilarity((data ?? []) as SimilarityHit[]);
      toast({ title: "Similarity scan complete", description: `${(data ?? []).length} potential matches.` });
    } catch (e: any) {
      toast({ title: "Scan failed", description: e.message, variant: "destructive" });
    } finally { setScanning(false); }
  };

  const approvedMappingCount = mappings.filter((m) => m.approval_status === "approved").length;
  const publicationBlockers = (() => {
    if (!editing) return [] as string[];
    const b: string[] = [];
    if (editing.content_grade === "guideline_backed") {
      if (approvedMappingCount < 1) b.push("At least one approved evidence mapping");
      if (!editing.clinical_review_date) b.push("Clinical review date");
      if (!editing.next_review_at) b.push("Next review date");
      if (!editing.originality_confirmed) b.push("Originality confirmation");
      if (!editing.clinical_reviewer_name) b.push("Clinical reviewer name");
    }
    return b;
  })();
  const canPublish = publicationBlockers.length === 0;


  const save = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      const payload = { ...editing };
      if (payload.status === "approved" && !payload.reviewed_at) {
        payload.reviewed_at = new Date().toISOString();
      }
      const { error } = editing.id
        ? await supabase.from("knowledge_topics").update(payload).eq("id", editing.id)
        : await supabase.from("knowledge_topics").insert(payload as any);
      if (error) throw error;
      toast({ title: "Saved" });
      setEditing(null);
      load();
    } catch (e: any) {
      toast({ title: "Save failed", description: e.message, variant: "destructive" });
    } finally { setSaving(false); }
  };

  const openLinks = async (t: Topic) => {
    setLinksFor(t);
    const { data } = await supabase
      .from("knowledge_topic_links")
      .select("id, relation, to:knowledge_topics!knowledge_topic_links_to_topic_id_fkey(id, title, slug)")
      .eq("from_topic_id", t.id);
    setLinks(data ?? []);
  };

  const addLink = async () => {
    if (!linksFor || !newLink.to) return;
    const { error } = await supabase.from("knowledge_topic_links").insert({
      from_topic_id: linksFor.id, to_topic_id: newLink.to, relation: newLink.relation,
    });
    if (error) { toast({ title: "Link failed", description: error.message, variant: "destructive" }); return; }
    setNewLink({ to: "", relation: "related_symptom" });
    openLinks(linksFor);
  };

  const removeLink = async (id: string) => {
    await supabase.from("knowledge_topic_links").delete().eq("id", id);
    if (linksFor) openLinks(linksFor);
  };

  const filtered = topics.filter(t =>
    (filter.pillar === "all" || t.pillar === Number(filter.pillar)) &&
    (filter.status === "all" || t.status === filter.status));

  if (loading) return <div className="flex items-center justify-center p-8"><Loader2 className="animate-spin" /></div>;

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle>Knowledge Topics ({topics.length})</CardTitle>
            <p className="text-sm text-muted-foreground mt-1">Symptom, treatment, medicine and concept pages powering the ARAG knowledge graph.</p>
          </div>
          <Button onClick={() => setEditing({ ...empty })}><Plus className="h-4 w-4 mr-1" />New Topic</Button>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex gap-3">
            <Select value={filter.pillar} onValueChange={v => setFilter(f => ({ ...f, pillar: v }))}>
              <SelectTrigger className="w-64"><SelectValue placeholder="Pillar" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All pillars</SelectItem>
                {PILLARS.map(p => <SelectItem key={p.n} value={String(p.n)}>{p.n}. {p.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={filter.status} onValueChange={v => setFilter(f => ({ ...f, status: v }))}>
              <SelectTrigger className="w-48"><SelectValue placeholder="Status" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="review">In review</SelectItem>
                <SelectItem value="approved">Approved</SelectItem>
                <SelectItem value="retired">Retired</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {filtered.length === 0 ? (
            <p className="text-sm text-muted-foreground p-4 text-center">No topics yet. Create the first one.</p>
          ) : (
            <div className="divide-y">
              {filtered.map(t => {
                const tier = tiers.find(x => x.code === t.evidence_level);
                return (
                  <div key={t.id} className="py-3 flex items-center gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium">{t.title}</span>
                        <Badge variant="outline">P{t.pillar}</Badge>
                        <Badge variant="secondary">{t.topic_type}</Badge>
                        {tier && <Badge variant="outline" className="text-foreground/70">{tier.label}</Badge>}
                        <Badge variant={t.status === "approved" ? "default" : "outline"}>{t.status}</Badge>
                      </div>
                      <p className="text-xs text-muted-foreground truncate mt-0.5">/{t.slug}</p>
                    </div>
                    {t.status === "approved" && (
                      <Button size="sm" variant="ghost" asChild>
                        <a href={`/knowledge/${t.slug}`} target="_blank" rel="noreferrer"><ExternalLink className="h-3.5 w-3.5" /></a>
                      </Button>
                    )}
                    <Button size="sm" variant="ghost" onClick={() => openLinks(t)}><Link2 className="h-3.5 w-3.5" /></Button>
                    <Button size="sm" variant="ghost" onClick={() => setEditing(t)}><Pencil className="h-3.5 w-3.5" /></Button>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Edit dialog */}
      <Dialog open={!!editing} onOpenChange={o => !o && setEditing(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing?.id ? "Edit topic" : "New topic"}</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Title</Label><Input value={editing.title ?? ""} onChange={e => setEditing({ ...editing, title: e.target.value })} /></div>
                <div><Label>Slug</Label><Input value={editing.slug ?? ""} onChange={e => setEditing({ ...editing, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-") })} /></div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div><Label>Pillar</Label>
                  <Select value={String(editing.pillar ?? 1)} onValueChange={v => setEditing({ ...editing, pillar: Number(v) })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{PILLARS.map(p => <SelectItem key={p.n} value={String(p.n)}>{p.n}. {p.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Type</Label>
                  <Select value={editing.topic_type ?? "concept"} onValueChange={v => setEditing({ ...editing, topic_type: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Evidence</Label>
                  <Select value={editing.evidence_level ?? "moderate"} onValueChange={v => setEditing({ ...editing, evidence_level: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{tiers.map(t => <SelectItem key={t.code} value={t.code}>{t.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div><Label>Plain-language summary (2-3 sentences, Grade 7-8)</Label>
                <Textarea rows={3} value={editing.summary_plain ?? ""} onChange={e => setEditing({ ...editing, summary_plain: e.target.value })} />
              </div>
              <div><Label>Body (Markdown)</Label>
                <Textarea rows={10} value={editing.body_md ?? ""} onChange={e => setEditing({ ...editing, body_md: e.target.value })} />
              </div>
              <div><Label>Evidence rationale</Label>
                <Textarea rows={2} value={editing.evidence_rationale ?? ""} onChange={e => setEditing({ ...editing, evidence_rationale: e.target.value })} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>When to see a clinician (red flags)</Label>
                  <Textarea rows={4} value={editing.red_flags_md ?? ""} onChange={e => setEditing({ ...editing, red_flags_md: e.target.value })} />
                </div>
                <div><Label>Doctor discussion points</Label>
                  <Textarea rows={4} value={editing.doctor_questions_md ?? ""} onChange={e => setEditing({ ...editing, doctor_questions_md: e.target.value })} />
                </div>
              </div>

              {/* Provenance & governance */}
              <div className="border-t pt-3 mt-2">
                <div className="flex items-center gap-2 mb-3">
                  <ShieldCheck className="h-4 w-4 text-muted-foreground" />
                  <h3 className="font-semibold">Provenance &amp; governance</h3>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Content grade</Label>
                    <Select value={editing.content_grade ?? "editorial"} onValueChange={v => setEditing({ ...editing, content_grade: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>{CONTENT_GRADES.map(g => <SelectItem key={g.code} value={g.code}>{g.label}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div><Label>Author name</Label>
                    <Input value={editing.author_name ?? ""} onChange={e => setEditing({ ...editing, author_name: e.target.value })} />
                  </div>
                  <div><Label>Clinical reviewer</Label>
                    <Input value={editing.clinical_reviewer_name ?? ""} onChange={e => setEditing({ ...editing, clinical_reviewer_name: e.target.value })} />
                  </div>
                  <div><Label>Clinical review date</Label>
                    <Input type="date" value={editing.clinical_review_date ?? ""} onChange={e => setEditing({ ...editing, clinical_review_date: e.target.value || null })} />
                  </div>
                  <div><Label>Next review date</Label>
                    <Input type="date" value={editing.next_review_at ?? ""} onChange={e => setEditing({ ...editing, next_review_at: e.target.value || null })} />
                  </div>
                  <div className="col-span-2 flex items-start gap-2 rounded-md border p-3 bg-muted/30">
                    <Checkbox id="originality" checked={!!editing.originality_confirmed} onCheckedChange={v => setEditing({ ...editing, originality_confirmed: !!v })} />
                    <Label htmlFor="originality" className="text-sm leading-snug">
                      I confirm this content was written in original Her Season language, is not copied or lightly paraphrased from any external source, and any evidence citations are attributed transparently.
                    </Label>
                  </div>
                </div>
              </div>

              {/* Similarity scan */}
              <div className="border-t pt-3 mt-2">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Search className="h-4 w-4 text-muted-foreground" />
                    <h3 className="font-semibold">Similarity scan vs ingested sources</h3>
                  </div>
                  <Button size="sm" variant="outline" onClick={runSimilarityScan} disabled={scanning}>
                    {scanning ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Search className="h-3.5 w-3.5 mr-1" />}
                    Run scan
                  </Button>
                </div>
                {similarity === null ? (
                  <p className="text-xs text-muted-foreground">Run a scan to flag paragraphs that resemble ingested clinical source text. Human review still required.</p>
                ) : similarity.length === 0 ? (
                  <p className="text-xs text-emerald-700 flex items-center gap-1"><ShieldCheck className="h-3.5 w-3.5" /> No high-similarity matches found.</p>
                ) : (
                  <div className="space-y-2">
                    <div className="text-xs text-amber-700 flex items-center gap-1"><AlertTriangle className="h-3.5 w-3.5" /> Possible overlap — rewrite in original wording before publishing.</div>
                    {similarity.map(h => (
                      <div key={h.chunk_id} className="text-xs border rounded p-2 bg-amber-50/50">
                        <div className="flex justify-between mb-1">
                          <span className="font-medium">{h.organisation} — {h.document_title}</span>
                          <Badge variant="outline">{(h.similarity * 100).toFixed(0)}% similar</Badge>
                        </div>
                        <p className="text-muted-foreground line-clamp-3">{h.content_excerpt}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Evidence mappings */}
              {editing.id && (
                <div className="border-t pt-3 mt-2">
                  <div className="flex items-center gap-2 mb-2">
                    <BookOpenCheck className="h-4 w-4 text-muted-foreground" />
                    <h3 className="font-semibold">Evidence mappings ({mappings.length}, {approvedMappingCount} approved)</h3>
                  </div>
                  <div className="space-y-2">
                    {mappings.map(m => {
                      const doc = sourceDocs.find(d => d.id === m.document_id);
                      return (
                        <div key={m.id} className="text-xs border rounded p-2 space-y-1">
                          <div className="flex items-center justify-between gap-2">
                            <span className="font-medium">{m.claim}</span>
                            <div className="flex items-center gap-1">
                              <Select value={m.approval_status} onValueChange={v => updateMappingStatus(m.id, v)}>
                                <SelectTrigger className="h-7 w-28"><SelectValue /></SelectTrigger>
                                <SelectContent>{MAPPING_STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                              </Select>
                              <Button size="sm" variant="ghost" onClick={() => removeMapping(m.id)}>×</Button>
                            </div>
                          </div>
                          {m.her_season_wording && <p className="text-muted-foreground italic">Her Season: "{m.her_season_wording}"</p>}
                          <p className="text-muted-foreground">
                            Source: {doc ? `${doc.organisation} — ${doc.title}` : "—"}
                            {m.source_location && ` · ${m.source_location}`}
                            {m.evidence_class && ` · ${m.evidence_class}`}
                          </p>
                        </div>
                      );
                    })}
                    <div className="border rounded p-2 space-y-2 bg-muted/30">
                      <Input placeholder="Claim (short factual statement)" value={newMapping.claim ?? ""} onChange={e => setNewMapping({ ...newMapping, claim: e.target.value })} />
                      <Textarea rows={2} placeholder="Her Season wording (how we say it)" value={newMapping.her_season_wording ?? ""} onChange={e => setNewMapping({ ...newMapping, her_season_wording: e.target.value })} />
                      <div className="grid grid-cols-2 gap-2">
                        <Select value={newMapping.document_id ?? ""} onValueChange={v => setNewMapping({ ...newMapping, document_id: v })}>
                          <SelectTrigger><SelectValue placeholder="Source document" /></SelectTrigger>
                          <SelectContent>{sourceDocs.map(d => <SelectItem key={d.id} value={d.id}>{d.organisation} — {d.title}</SelectItem>)}</SelectContent>
                        </Select>
                        <Input placeholder="Location (page/section)" value={newMapping.source_location ?? ""} onChange={e => setNewMapping({ ...newMapping, source_location: e.target.value })} />
                        <Select value={newMapping.evidence_class ?? "primary_source"} onValueChange={v => setNewMapping({ ...newMapping, evidence_class: v })}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>{EVIDENCE_CLASSES.map(c => <SelectItem key={c} value={c}>{c.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
                        </Select>
                        <Button size="sm" onClick={addMapping} disabled={!newMapping.claim}>Add mapping</Button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Status + blockers */}
              <div className="border-t pt-3 mt-2 space-y-3">
                <div><Label>Status</Label>
                  <Select value={editing.status ?? "draft"} onValueChange={v => setEditing({ ...editing, status: v })}>
                    <SelectTrigger className="w-56"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="draft">Draft</SelectItem>
                      <SelectItem value="editorial_review">Editorial review</SelectItem>
                      <SelectItem value="clinical_review">Clinical review</SelectItem>
                      <SelectItem value="approved" disabled={!canPublish}>Approved (live)</SelectItem>
                      <SelectItem value="retired">Retired</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {editing.content_grade === "guideline_backed" && (
                  publicationBlockers.length > 0 ? (
                    <div className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm">
                      <div className="flex items-center gap-2 font-medium text-destructive mb-1"><ShieldAlert className="h-4 w-4" /> Publication blocked</div>
                      <p className="text-xs text-muted-foreground mb-2">Guideline-backed content requires the following before it can go live:</p>
                      <ul className="text-xs list-disc pl-5 space-y-0.5">
                        {publicationBlockers.map(b => <li key={b}>{b}</li>)}
                      </ul>
                    </div>
                  ) : (
                    <div className="rounded-md border border-emerald-500/40 bg-emerald-500/5 p-3 text-sm text-emerald-800 flex items-center gap-2">
                      <ShieldCheck className="h-4 w-4" /> All governance checks passed. Ready to publish.
                    </div>
                  )
                )}
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
                <Button onClick={save} disabled={saving || (editing.status === "approved" && !canPublish)}>
                  {saving && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}Save
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Links dialog */}
      <Dialog open={!!linksFor} onOpenChange={o => !o && setLinksFor(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Graph links — {linksFor?.title}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            {links.map((l: any) => (
              <div key={l.id} className="flex items-center gap-2 text-sm">
                <Badge variant="outline">{l.relation}</Badge>
                <span className="flex-1">→ {l.to?.title}</span>
                <Button size="sm" variant="ghost" onClick={() => removeLink(l.id)}>Remove</Button>
              </div>
            ))}
            <div className="flex gap-2 items-end border-t pt-3">
              <div className="flex-1">
                <Label className="text-xs">Link to topic</Label>
                <Select value={newLink.to} onValueChange={v => setNewLink({ ...newLink, to: v })}>
                  <SelectTrigger><SelectValue placeholder="Select topic" /></SelectTrigger>
                  <SelectContent>
                    {topics.filter(t => t.id !== linksFor?.id).map(t => (
                      <SelectItem key={t.id} value={t.id}>{t.title}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Relation</Label>
                <Select value={newLink.relation} onValueChange={v => setNewLink({ ...newLink, relation: v })}>
                  <SelectTrigger className="w-56"><SelectValue /></SelectTrigger>
                  <SelectContent>{RELATIONS.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <Button onClick={addLink}>Add</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
